// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { SparseAsset, sparseAssetSchema } from '@/asset/api/types';
import { z } from 'zod';

/**
 * Schema for validating the response from the check serial API endpoint.
 */
const checkSerialResponseSchema = z.object({
  data: sparseAssetSchema,
  metadata: responseMetadataSchema,
});

/**
 * Checks if a serial number exists in the system.
 *
 * @param serial - The serial number to check
 * @returns The sparse asset if found, null if not found (404)
 * @throws Error for other HTTP errors
 */
export async function checkSerial(serial: string): Promise<SparseAsset | null> {
  try {
    const url = '/v1/asset-tools/check-serial';
    const response = await api.post(url, { serial });

    const { data } = await parseResponse(
      checkSerialResponseSchema,
      response.data,
      {
        schemaName: 'CheckSerial',
        endpoint: url,
      }
    );
    return data;
  } catch (error) {
    // If it's a 404, return null (serial not found)
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response &&
      error.response.status === 404
    ) {
      return null;
    }
    // Re-throw other errors
    throw error;
  }
}
